import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";

const includesHeathrow = (quote) => {
  const route = `${quote?.request?.pickup || ""} ${quote?.request?.dropoff || ""}`.toUpperCase();
  return route.includes("HEATHROW") || route.includes("HOUNSLOW") || route.replace(/[^A-Z0-9]/g, "").includes("TW6");
};

const ResultsLiveTrustStrip = ({ brand = "ccg", quote, paused = false }) => {
  const [live, setLive] = useState(null);
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/api/analytics/v2/trust-strip")
      .then((response) => {
        if (active && response.data?.success) setLive(response.data);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const signals = useMemo(() => {
    const isCoach = brand === "coachgrab" || brand === "cheapcoachgrab";
    const isGroup = isCoach || brand === "minibusgrab";
    const items = [
      {
        icon: "✓",
        label: "Licensed operation",
        text: isGroup ? "PSV-licensed operating partner • PF2071924" : "Licensed UK private-hire operators",
      },
      { icon: "↗", label: "Connected data", text: "Live National Rail data partner" },
      { icon: "●", label: "Live intelligence", text: "Live road, rail, flight & weather checks" },
    ];

    if (live?.recent_quote?.pickup && live?.recent_quote?.dropoff) {
      items.unshift({
        icon: "●",
        label: "Recent activity",
        text: `Recent quote checked: ${live.recent_quote.pickup} → ${live.recent_quote.dropoff}`,
      });
    }
    if (Number(live?.today_quote_count) > 5) {
      items.push({ icon: "✓", label: "Checked today", text: `${live.today_quote_count} journeys checked today` });
    }
    if (includesHeathrow(quote) && Number(live?.heathrow?.flights_monitored) > 0) {
      const cancellations = Number(live.heathrow.cancellations_tracked) || 0;
      items.push({
        icon: "✈",
        label: "Heathrow live",
        text: `Heathrow today: ${live.heathrow.flights_monitored.toLocaleString("en-GB")} flights${cancellations ? ` • ${cancellations} cancellations` : ""}`,
      });
    }
    if (includesHeathrow(quote) && Number.isFinite(Number(live?.heathrow?.temperature_c))) {
      items.push({
        icon: "°",
        label: "Heathrow weather",
        text: `${Number(live.heathrow.temperature_c).toFixed(1)}°C at Heathrow • Live weather monitoring`,
      });
    }
    return items.slice(0, 6);
  }, [brand, live, quote]);

  useEffect(() => {
    setIndex(0);
  }, [signals.length]);

  useEffect(() => {
    if (paused || hovered || signals.length < 2) return undefined;
    const timer = window.setInterval(() => setIndex((current) => (current + 1) % signals.length), 6000);
    return () => window.clearInterval(timer);
  }, [hovered, paused, signals.length]);

  const item = signals[index % signals.length];
  if (!item) return null;

  return (
    <section
      className="premium-live-trust-strip"
      aria-label="Live trust and journey information"
      aria-live="off"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <span className="premium-live-trust-icon" aria-hidden="true">{item.icon}</span>
      <small>{item.label}</small>
      <strong key={`${item.label}-${index}`}>{item.text}</strong>
      <i aria-hidden="true">Live</i>
    </section>
  );
};

export default ResultsLiveTrustStrip;
